import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import PasswordResetForm from './components/PasswordResetForm';
import { supabase } from '../../lib/supabase';

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenValid, setTokenValid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    // Check if we have a valid session from the reset link
    const checkResetToken = async () => {
      try {
        // Supabase automatically handles the token from the URL
        const { data: { session }, error } = await supabase?.auth?.getSession();
        
        if (error || !session) {
          setTokenValid(false);
        } else {
          setTokenValid(true);
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setTokenValid(false);
      } finally {
        setLoading(false);
      }
    };

    checkResetToken();
  }, [location]);

  const handleResetSuccess = () => {
    setResetSuccess(true);
    // Redirect to login after 3 seconds
    setTimeout(() => {
      navigate('/user-login');
    }, 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Validating reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Simple Logo Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src="/assets/images/90A19BF4-3446-4543-87E4-2148054C81C1-1768952058269.jpeg" alt="OfferSense Logo" className="w-10 h-10 object-contain" />
            <span className="text-xl font-semibold">
              <span className="text-[#2C3E5F]">OfferSense</span>
              <span className="text-[#5DBFB3]">.Ai</span>
            </span>
          </div>
        </div>
      </div>

      <main className="main-content">
        <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4 py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-md">
            <div className="bg-card rounded-xl shadow-elevation-lg p-6 md:p-8 lg:p-10 border border-border">
              {tokenValid === false ? (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
                      <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                      Invalid or Expired Link
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground mb-6">
                      This password reset link is invalid or has expired. Reset links are only valid for 1 hour.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={() => navigate('/forgot-password')}
                      className="w-full py-3 px-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                    >
                      Request New Reset Link
                    </button>
                    <button
                      onClick={() => navigate('/user-login')}
                      className="w-full py-3 px-4 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-medium"
                    >
                      Back to Login
                    </button>
                  </div>
                </>
              ) : resetSuccess ? (
                <>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
                      <Icon name="CheckCircle" size={32} color="#10b981" />
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                      Password Reset Successful!
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground mb-6">
                      Your password has been updated successfully. You can now sign in with your new password.
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Loader2" size={16} className="animate-spin" />
                      Redirecting to login...
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-6 md:mb-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="Key" size={32} color="var(--color-primary)" />
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                      Reset Your Password
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Enter your new password below. Make sure it's strong and secure.
                    </p>
                  </div>

                  <PasswordResetForm onSuccess={handleResetSuccess} />
                </>
              )}
            </div>

            {!resetSuccess && (
              <div className="mt-6 md:mt-8 text-center">
                <p className="text-xs md:text-sm text-muted-foreground">
                  Need help? Contact our support team
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} OfferSense. All rights reserved.
            </p>
            <div className="flex items-center gap-4 md:gap-6">
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PasswordReset;